import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ImageOff, Pencil, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p ? `${BACKEND}${p}` : "");
const blank = { title: "", description: "", price_text: "", offer_type: "today", start_date: "", end_date: "", branch_id: "", book_now: true, active: true };

export default function OffersManager() {
  const [offers, setOffers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/offers").then((r) => setOffers(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const reset = () => { setForm(blank); setEditId(null); setFile(null); if (fileRef.current) fileRef.current.value = ""; };
  const startEdit = (o) => {
    setEditId(o.id);
    setForm({ title: o.title || "", description: o.description || "", price_text: o.price_text || "", offer_type: o.offer_type || "today",
      start_date: o.start_date || "", end_date: o.end_date || "", branch_id: o.branch_id || "", book_now: o.book_now !== false, active: o.active !== false });
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Offer title is required"); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : (v ?? "")));
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      if (editId) await api.put(`/offers/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/offers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editId ? "Offer updated" : "Offer created"); reset(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (o) => {
    const fd = new FormData(); fd.append("active", String(!(o.active !== false)));
    try { await api.put(`/offers/${o.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (o) => {
    if (!window.confirm("Remove this offer? Its history is not shown on the site once removed.")) return;
    try { await api.delete(`/offers/${o.id}`); toast.success("Offer removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const branchName = (id) => id ? (branches.find((b) => b.id === id)?.name || "Branch") : "All Branches";

  return (
    <div>
      <PageHeader title="Offer Management" subtitle="Today's & Ongoing offers shown on your public booking home" />

      <Card className="bg-card border-border p-5 mb-6 max-w-3xl" data-testid="offer-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg">{editId ? "Edit Offer" : "New Offer"}</h3>
          {editId && <Button size="sm" variant="ghost" onClick={reset} data-testid="offer-cancel-edit"><X size={14} className="mr-1" />Cancel</Button>}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Keratin Treatment — Flat 20% OFF" className="bg-secondary mt-1" data-testid="offer-title" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Valid today only. Includes wash & blow-dry." className="bg-secondary mt-1" data-testid="offer-desc" /></div>
          <div><Label className="text-xs">Price / Discount text</Label>
            <Input value={form.price_text} onChange={(e) => setForm({ ...form, price_text: e.target.value })} placeholder="₹1,999 or Flat 20% OFF" className="bg-secondary mt-1" data-testid="offer-price" /></div>
          <div><Label className="text-xs">Offer type</Label>
            <Select value={form.offer_type} onValueChange={(v) => setForm({ ...form, offer_type: v })}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="offer-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="today">Today's Offer</SelectItem><SelectItem value="ongoing">Ongoing Offer</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-xs">Start date</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-secondary mt-1" data-testid="offer-start" /></div>
          <div><Label className="text-xs">End date</Label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-secondary mt-1" data-testid="offer-end" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Branch</Label>
            <Select value={form.branch_id || "all"} onValueChange={(v) => setForm({ ...form, branch_id: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="offer-branch"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="flex items-center gap-2"><Switch checked={form.book_now} onCheckedChange={(v) => setForm({ ...form, book_now: v })} data-testid="offer-booknow-toggle" /><Label className="text-xs">Show "Book Now" button</Label></div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="offer-active-toggle" /><Label className="text-xs">Active</Label></div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Banner image (optional)</Label>
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block text-sm mt-1 text-muted-foreground" data-testid="offer-file" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="offer-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#E6C687] mt-4">
          <Plus size={16} className="mr-1" /> {saving ? "Saving…" : editId ? "Update Offer" : "Create Offer"}
        </Button>
      </Card>

      {offers.length === 0 ? (
        <Card className="bg-card border-border p-10 text-center text-muted-foreground" data-testid="offers-empty"><ImageOff className="mx-auto mb-3 opacity-50" size={28} />No offers yet.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {offers.map((o) => (
            <Card key={o.id} className="bg-card border-border overflow-hidden" data-testid={`offer-card-${o.id}`}>
              {o.image ? <div className="aspect-[3/1] bg-secondary overflow-hidden"><img src={imgUrl(o.image)} alt={o.title} className="w-full h-full object-cover" /></div>
                : <div className="aspect-[3/1] bg-gradient-to-br from-[#1a1508] to-[#0A0A0A] flex items-center justify-center text-primary/60 text-xs uppercase tracking-widest">{o.offer_type === "ongoing" ? "Ongoing Offer" : "Today's Offer"}</div>}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-[10px] uppercase tracking-wide text-primary">{o.offer_type === "ongoing" ? "Ongoing" : "Today's"}</span>
                    <p className="font-medium truncate">{o.title || "Untitled"}</p>
                    {o.price_text && <p className="text-primary text-sm">{o.price_text}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{branchName(o.branch_id)}{o.start_date || o.end_date ? ` · ${o.start_date || "…"} → ${o.end_date || "…"}` : ""}</p>
                    <p className={`text-xs mt-0.5 ${o.active !== false ? "text-green-400" : "text-muted-foreground"}`}>{o.active !== false ? "Live" : "Hidden"}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={o.active !== false} onCheckedChange={() => toggle(o)} data-testid={`offer-toggle-${o.id}`} />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => startEdit(o)} data-testid={`offer-edit-${o.id}`}><Pencil size={13} className="mr-1" />Edit</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(o)} data-testid={`offer-delete-${o.id}`}><Trash2 size={13} className="mr-1" />Remove</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
